import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Divider,
  Chip,
  Stack,
  Paper
} from '@mui/material';
import dayjs from 'dayjs';

// Icons
import NumbersIcon from '@mui/icons-material/Numbers';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import StraightenIcon from '@mui/icons-material/Straighten';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import HeightIcon from '@mui/icons-material/Height';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotesIcon from '@mui/icons-material/Notes';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import { useLanguage } from '../../../../utils/i18n';

export default function DetailTireDamage({ open, onClose, data }) {
  const { t } = useLanguage();
  if (!data) return null;

  const renderSectionHeader = (icon, title) => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
      {icon}
      <Typography variant="h6" fontWeight="bold" color="text.primary">
        {title}
      </Typography>
    </Stack>
  );

  const renderInfoItem = (icon, label, value, valColor = 'text.primary', valWeight = 'medium') => (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
      <Box sx={{ mt: 0.3, color: 'text.secondary' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        <Typography variant="body2" color={valColor} fontWeight={valWeight}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );

  // Mock timeline items based on the single inspection data if no real history array exists
  const timelineItems = [
    {
      date: data.install_date,
      title: t('tireRepairScrap.tireInstalled', 'Tire Installed'),
      description: `${t('tireRepairScrap.installedOn', 'Installed on')} ${data.last_vehicle || t('tireRepairScrap.vehicle', 'Vehicle')}`
    },
    {
      date: data.last_inspection?.inspection_date,
      title: t('tireRepairScrap.inspectionPerformed', 'Inspection Performed'),
      description: `${t('tireRepairScrap.damageReported', 'Damage reported')}: ${data.last_inspection?.tire_damage_name || 'N/A'} ${t('tireRepairScrap.by', 'by')} ${data.last_inspection?.inspector_name || 'System'}`
    },
    {
      date: data.remove_date,
      title: t('tireRepairScrap.tireRemoved', 'Tire Removed'),
      description: `${t('tireRepairScrap.removedFrom', 'Removed from')} ${data.last_vehicle || t('tireRepairScrap.vehicle', 'Vehicle')}`
    }
  ]
    .filter((item) => item.date)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ p: 3, pb: 2, backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="overline" color="text.secondary" fontWeight="bold" sx={{ letterSpacing: 1 }}>
              {t('tireRepairScrap.tireAssetDetails', 'Tire Asset Details')}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
              <Typography variant="h4" fontWeight="900" color="primary.main">
                {data.serial_number || t('tireRepairScrap.unknownSn', 'UNKNOWN-SN')}
              </Typography>
              {data.count_retread > 0 && (
                <Chip
                  label={`${t('tireRepairScrap.retread', 'Retread')}: ${data.count_retread}/3`}
                  size="small"
                  color={data.count_retread >= 3 ? 'error' : 'info'}
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {data.status_backlog === 'CRITICAL' && (
                <Chip
                  label={t('tireRepairScrap.criticalPriority', 'CRITICAL Priority')}
                  color="error"
                  size="small"
                  icon={<WarningAmberIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {data.status_backlog === 'HIGH' && (
                <Chip
                  label={t('tireRepairScrap.highPriority', 'High Priority')}
                  color="error"
                  size="small"
                  icon={<WarningAmberIcon />}
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {data.status_backlog === 'MEDIUM' && (
                <Chip
                  label={t('tireRepairScrap.mediumPriority', 'Medium Priority')}
                  color="warning"
                  size="small"
                  sx={{ fontWeight: 'bold' }}
                />
              )}
              {data.status_backlog === 'LOW' && (
                <Chip label={t('tireRepairScrap.lowPriority', 'Low Priority')} color="success" size="small" sx={{ fontWeight: 'bold' }} />
              )}
            </Stack>
          </Box>
          <Box textAlign="right">
            <Typography variant="caption" color="text.secondary" display="block">
              {t('tireRepairScrap.currentCondition', 'Current Condition')}
            </Typography>
            <Chip
              label={data.current_condition || 'N/A'}
              color={data.current_condition === 'Damaged' ? 'error' : 'default'}
              sx={{ fontWeight: 'bold', mt: 0.5 }}
            />
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 3, backgroundColor: '#f8fafc' }}>
        <Grid container spacing={3}>
          {/* LEFT COLUMN */}
          <Grid item size={{ xs: 12, md: 7 }}>
            <Stack spacing={3}>
              {/* Tire Information */}
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {renderSectionHeader(<InfoIcon color="primary" />, t('tireRepairScrap.tireInformation', 'Tire Information'))}
                <Grid container spacing={2}>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <BrandingWatermarkIcon fontSize="small" />,
                      t('specification.brand', 'Tire Brand'),
                      data.join?.tire_item_tire_brand_name
                    )}
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <StraightenIcon fontSize="small" />,
                      t('specification.size', 'Tire Size'),
                      data.join?.tire_item_tire_size_code_internal || data.join?.tire_item_tire_size_code || '-'
                    )}
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <ViewStreamIcon fontSize="small" />,
                      t('detailTireInventory.specPattern', 'Pattern'),
                      data.join?.m_tire_spec_pattern
                    )}
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <NumbersIcon fontSize="small" />,
                      t('specification.spec_name', 'Tire Specification'),
                      data.join?.tire_item_tire_spec_name
                    )}
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <LocalShippingIcon fontSize="small" />,
                      t('detailTireInventory.lastUnit', 'Last Vehicle'),
                      data.last_vehicle || '-'
                    )}
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6 }}>
                    {renderInfoItem(
                      <RadioButtonCheckedIcon fontSize="small" />,
                      t('detailTireInventory.lastPosition', 'Last Position'),
                      data.last_position || '-'
                    )}
                  </Grid>
                </Grid>
              </Paper>

              {/* Inspection Information */}
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                {renderSectionHeader(<HealthAndSafetyIcon color="primary" />, t('tireRepairScrap.inspectionResult', 'Inspection Result'))}
                {data.last_inspection ? (
                  <Grid container spacing={2}>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      {renderInfoItem(
                        <LocalHospitalIcon fontSize="small" />,
                        t('tireRepairScrap.damageType', 'Damage Type'),
                        data.last_inspection.tire_damage_name,
                        'error.main',
                        'bold'
                      )}
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      {renderInfoItem(
                        <HeightIcon fontSize="small" />,
                        t('tireCardHistory.treadDepthMm', 'Tread Depth') + ' (RTD)',
                        `${data.last_inspection.tread_depth || 0} mm`
                      )}
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      {renderInfoItem(
                        <PersonIcon fontSize="small" />,
                        t('tireRepairScrap.inspector', 'Inspector'),
                        data.last_inspection.inspector_name
                      )}
                    </Grid>
                    <Grid item size={{ xs: 12, sm: 6 }}>
                      {renderInfoItem(
                        <CalendarTodayIcon fontSize="small" />,
                        t('tireRepairScrap.inspectionDate', 'Inspection Date'),
                        data.last_inspection.inspection_date ? dayjs(data.last_inspection.inspection_date).format('DD MMM YYYY') : '-'
                      )}
                    </Grid>
                    <Grid item size={{ xs: 12 }}>
                      <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                      {renderInfoItem(
                        <NotesIcon fontSize="small" />,
                        t('tireRepairScrap.inspectionNotes', 'Inspection Notes'),
                        data.last_inspection.notes
                      )}
                    </Grid>
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary" fontStyle="italic">
                    {t('tireRepairScrap.noInspectionRecords', 'No inspection records found for this tire.')}
                  </Typography>
                )}
              </Paper>
            </Stack>
          </Grid>

          {/* RIGHT COLUMN */}
          <Grid item size={{ xs: 12, md: 5 }}>
            <Stack spacing={3}>
              {/* Timeline / Status History */}
              <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', height: '100%' }}>
                {renderSectionHeader(<TimelineIcon color="primary" />, t('tireRepairScrap.statusHistory', 'Status History'))}

                <Box sx={{ ml: 1, mt: 2, borderLeft: '2px solid #e2e8f0', pl: 3, position: 'relative' }}>
                  {timelineItems.map((item, idx) => (
                    <Box key={idx} sx={{ mb: 3, position: 'relative' }}>
                      {/* Timeline Dot */}
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -32,
                          top: 2,
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: idx === 0 ? 'primary.main' : '#cbd5e1',
                          border: '2px solid white',
                          boxShadow: '0 0 0 2px #f8fafc'
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" fontWeight="bold">
                        {dayjs(item.date).format('DD MMM YYYY')}
                      </Typography>
                      <Typography variant="body2" fontWeight="bold" color="text.primary" sx={{ mt: 0.5 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.description}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, backgroundColor: 'white', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ fontWeight: 'bold' }}>
          {t('tireRepairScrap.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
